import { createAsyncThunk } from "@reduxjs/toolkit"

import {
  acceptProposedCalendarTime,
  cancelPortalCalendarEvent,
  createPortalCalendarEvent,
  getPortalAlbums,
  getPortalCalendarEvents,
  getPortalDocuments,
  getPortalInvoices,
  getPortalPhotos,
  getPortalProfile,
  getPortalProjectDetail,
  getPortalProjects,
  requestPortalCalendarReschedule,
  rsvpToPortalCalendarEvent,
  updatePortalCalendarEvent,
  updatePortalProfile,
  updatePortalReminderPreference,
  uploadPortalDocument,
  uploadPortalPhoto,
  type CreatePortalCalendarEventPayload,
  type RequestCalendarRescheduleParams,
  type UpdatePortalCalendarEventPayload,
  type UpdatePortalProfilePayload,
  type UpdateReminderPreferencePayload
} from "../api/services.ts"
import type {
  PortalAlbum,
  PortalCalendarEvent,
  PortalCrossProjectDocument,
  PortalCrossProjectInvoice,
  PortalCrossProjectPhoto,
  PortalCustomerProfile,
  PortalDocument,
  PortalInvoice,
  PortalPhoto,
  PortalProjectDetail,
  PortalProjectListItem,
  PortalRsvpResponse
} from "../types/portal.types.ts"

export const fetchPortalProjects = createAsyncThunk<PortalProjectListItem[]>(
  "portal/fetchProjects",
  async (_, { rejectWithValue }) => {
    const response = await getPortalProjects()
    if (!response.ok) return rejectWithValue(response.data)
    return response.data?.results ?? []
  }
)

export const fetchPortalProjectDetail = createAsyncThunk<
  PortalProjectDetail,
  string
>("portal/fetchProjectDetail", async (projectId, { rejectWithValue }) => {
  const response = await getPortalProjectDetail(projectId)
  if (!response.ok || !response.data) return rejectWithValue(response.data)
  return response.data
})

export const fetchPortalInvoices = createAsyncThunk<PortalInvoice[], string>(
  "portal/fetchInvoices",
  async (projectId, { rejectWithValue }) => {
    const response = await getPortalInvoices(projectId)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data?.results ?? []
  }
)

// Backs both Home's "Recent documents" and the Documents page's full list —
// one fetch, one array in the store, so the two surfaces can never disagree
// about what is actually shared.
export const fetchPortalDocuments = createAsyncThunk<PortalDocument[], string>(
  "portal/fetchDocuments",
  async (projectId, { rejectWithValue }) => {
    const response = await getPortalDocuments(projectId)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data?.results ?? []
  }
)

interface UploadDocumentArgs {
  projectId: string
  file: File
  note?: string
}

export const uploadDocumentThunk = createAsyncThunk<PortalDocument, UploadDocumentArgs>(
  "portal/uploadDocument",
  async (args, { rejectWithValue }) => {
    const response = await uploadPortalDocument(args)
    if (!response.ok || !response.data) return rejectWithValue(response.data)
    return response.data.details.data
  }
)

export const fetchPortalAlbums = createAsyncThunk<PortalAlbum[], string>(
  "portal/fetchAlbums",
  async (projectId, { rejectWithValue }) => {
    const response = await getPortalAlbums(projectId)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data?.results ?? []
  }
)

export const fetchPortalPhotos = createAsyncThunk<PortalPhoto[], string>(
  "portal/fetchPhotos",
  async (projectId, { rejectWithValue }) => {
    const response = await getPortalPhotos(projectId)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data ?? []
  }
)

interface UploadPhotoArgs {
  projectId: string
  file: File
}

export const uploadPhotoThunk = createAsyncThunk<PortalPhoto, UploadPhotoArgs>(
  "portal/uploadPhoto",
  async (args, { rejectWithValue }) => {
    const response = await uploadPortalPhoto(args)
    if (!response.ok || !response.data) return rejectWithValue(response.data)
    return response.data.details.data
  }
)

// "All projects" — Needs your attention. Fans out one invoices fetch per
// accessible project and merges the outstanding ones with their project
// name attached, so every row is traceable and nothing mixes currencies
// across projects (each project's own invoices already share one currency;
// this never sums across them, only lists them).
export const fetchAllProjectsInvoices = createAsyncThunk<
  PortalCrossProjectInvoice[],
  { id: string; lead_name: string }[]
>("portal/fetchAllProjectsInvoices", async (projects) => {
  const results = await Promise.all(
    projects.map(async (project) => {
      const response = await getPortalInvoices(project.id)
      if (!response.ok) return []
      return (response.data?.results ?? []).map((invoice) => ({
        ...invoice,
        project_id: project.id,
        project_name: project.lead_name
      }))
    })
  )
  return results.flat()
})

// "All projects" — Recent updates. Same fan-out pattern as invoices.
export const fetchAllProjectsDocuments = createAsyncThunk<
  PortalCrossProjectDocument[],
  { id: string; lead_name: string }[]
>("portal/fetchAllProjectsDocuments", async (projects) => {
  const results = await Promise.all(
    projects.map(async (project) => {
      const response = await getPortalDocuments(project.id)
      if (!response.ok) return []
      return (response.data?.results ?? []).map((document) => ({
        ...document,
        project_id: project.id,
        project_name: project.lead_name
      }))
    })
  )
  return results.flat()
})

// "All projects" — Photos grid. Same fan-out pattern as documents; flattens
// each project's customer-visible photos (GC-shared and customer-uploaded
// together, exactly what `photos` holds per-project) with the project attached.
export const fetchAllProjectsPhotos = createAsyncThunk<
  PortalCrossProjectPhoto[],
  { id: string; lead_name: string }[]
>("portal/fetchAllProjectsPhotos", async (projects) => {
  const results = await Promise.all(
    projects.map(async (project) => {
      const response = await getPortalPhotos(project.id)
      if (!response.ok) return []
      return (response.data ?? []).map((photo) => ({
        ...photo,
        project_id: project.id,
        project_name: project.lead_name
      }))
    })
  )
  return results.flat()
})

export const fetchPortalProfile = createAsyncThunk<PortalCustomerProfile>(
  "portal/fetchProfile",
  async (_, { rejectWithValue }) => {
    const response = await getPortalProfile()
    if (!response.ok || !response.data) return rejectWithValue(response.data)
    return response.data
  }
)

// GET /calendars/calendars/portal/events/ — the backend endpoint already
// aggregates across every project the customer is authorized on when no
// `project` filter is passed, so (unlike documents/photos/invoices) there is
// no need for a client-side per-project fan-out here.
export const fetchPortalCalendarEvents = createAsyncThunk<
  PortalCalendarEvent[],
  { projectId?: string | null; start?: string; end?: string } | undefined
>("portal/fetchCalendarEvents", async (params, { rejectWithValue }) => {
  const response = await getPortalCalendarEvents({
    project: params?.projectId ?? undefined,
    start: params?.start,
    end: params?.end
  })
  if (!response.ok) return rejectWithValue(response.data)
  return response.data ?? []
})

export const rsvpToCalendarEventThunk = createAsyncThunk<
  PortalCalendarEvent,
  { eventId: string; response: PortalRsvpResponse }
>("portal/rsvpToCalendarEvent", async ({ eventId, response: rsvpResponse }, { rejectWithValue }) => {
  const response = await rsvpToPortalCalendarEvent(eventId, rsvpResponse)
  if (!response.ok || !response.data) return rejectWithValue(response.data)
  return response.data.details.data
})

export const requestCalendarRescheduleThunk = createAsyncThunk<
  PortalCalendarEvent,
  RequestCalendarRescheduleParams
>("portal/requestCalendarReschedule", async (params, { rejectWithValue }) => {
  const response = await requestPortalCalendarReschedule(params)
  if (!response.ok || !response.data) return rejectWithValue(response.data)
  return response.data.details.data
})

export const createCalendarEventThunk = createAsyncThunk<
  PortalCalendarEvent,
  CreatePortalCalendarEventPayload,
  { rejectValue: { message?: string } }
>("portal/createCalendarEvent", async (payload, { rejectWithValue }) => {
  const response = await createPortalCalendarEvent(payload)
  if (!response.ok || !response.data) {
    return rejectWithValue({ message: response.data?.details?.message })
  }
  return response.data.details.data
})

export const updateCalendarEventThunk = createAsyncThunk<
  PortalCalendarEvent,
  { eventId: string; payload: UpdatePortalCalendarEventPayload }
>("portal/updateCalendarEvent", async ({ eventId, payload }, { rejectWithValue }) => {
  const response = await updatePortalCalendarEvent(eventId, payload)
  if (!response.ok || !response.data) return rejectWithValue(response.data)
  return response.data.details.data
})

export const cancelCalendarEventThunk = createAsyncThunk<PortalCalendarEvent, string>(
  "portal/cancelCalendarEvent",
  async (eventId, { rejectWithValue }) => {
    const response = await cancelPortalCalendarEvent(eventId)
    if (!response.ok || !response.data) return rejectWithValue(response.data)
    return response.data.details.data
  }
)

export const acceptProposedTimeThunk = createAsyncThunk<PortalCalendarEvent, string>(
  "portal/acceptProposedTime",
  async (eventId, { rejectWithValue }) => {
    const response = await acceptProposedCalendarTime(eventId)
    if (!response.ok || !response.data) return rejectWithValue(response.data)
    return response.data.details.data
  }
)

export const updateReminderPreferenceThunk = createAsyncThunk<
  PortalCalendarEvent,
  { eventId: string; payload: UpdateReminderPreferencePayload }
>("portal/updateReminderPreference", async ({ eventId, payload }, { rejectWithValue }) => {
  const response = await updatePortalReminderPreference(eventId, payload)
  if (!response.ok || !response.data) return rejectWithValue(response.data)
  return response.data.details.data
})

export const updateProfileThunk = createAsyncThunk<
  PortalCustomerProfile,
  UpdatePortalProfilePayload,
  { rejectValue: { conflict: boolean; message?: string } }
>("portal/updateProfile", async (payload, { rejectWithValue }) => {
  const response = await updatePortalProfile(payload)
  if (response.status === 409) {
    return rejectWithValue({ conflict: true })
  }
  if (!response.ok || !response.data) {
    return rejectWithValue({
      conflict: false,
      message: response.data?.details?.message
    })
  }
  return response.data.details.data
})
